import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { List } from 'immutable';
import { every, find, noop } from 'lodash';
import { Subject } from 'rxjs';
import { DialogService } from '../../../dialog/dialog.service';
import { IEntityDataDispatcher } from '../../../entity-data/entity-data-dispatcher.interface';
import { IError } from '../../../entity-data/entity-data.data';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { DropdownHorizontalAlignmentsEnum } from '../../../shared/components/dropdown/dropdown.component';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import { IRouter } from '../../../shared/interfaces/router.interface';
import { IToasterConfig, IToasterData } from '../../../toaster/toaster.data';
import { ToasterDispatcher } from '../../../toaster/toaster.dispatcher';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { DeleteEntityToastComponent } from '../delete-entity-toast/delete-entity-toast.component';
import { MasterListItemBuilder } from './master-list-item.builder';
import {
  IMasterListItemAction,
  IMasterListItemData,
} from './master-list-item.interface';
import { take } from 'rxjs/operators';

export interface INotifyOfDeleteOptions {
  dispatcher?: IEntityDataDispatcher;
  entity: any;
  context: IToasterData;
}

export interface ITokenValue {
  highlighted: boolean;
  value: string | number;
  isHtml?: boolean;
  truncateThreshold?: number;
}

@Component({
  selector: 'app-master-list-item',
  templateUrl: 'master-list-item.component.html',
  styleUrls: ['master-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [MasterListItemBuilder],
})
export class MasterListItemComponent implements OnInit, OnChanges, OnDestroy {
  @Input() type: string;
  @Input() id: string;
  @Input() route: IRouter;
  @Input() entityData: List<IMasterListItemData>;
  @Input() actions: List<List<IMasterListItemAction>>;
  @Input() featureName: string;
  @Input() listFilter: string;
  @Input() notifyOfDeleteOptions: INotifyOfDeleteOptions;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingAction: ITrackingAction;
  @Input() trackingAdditionalProperties: ITrackingProperties;
  @Input() position: number;
  @Input() bulkActionControl: FormControl;
  @Input() dropdownE2eId: string;

  @Output() onPrimaryAction: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  onAction: EventEmitter<IMasterListItemAction> = new EventEmitter<IMasterListItemAction>();
  @Output() onSelectClick: EventEmitter<boolean> = new EventEmitter<boolean>();

  @HostBinding('class.is-selected')
  get isSelected() {
    return this.bulkActionControl ? this.bulkActionControl.value : false;
  }

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _deletedTrackingAction: any;

  tokenizedValues: List<List<List<ITokenValue>>>;
  actionsHorizontalAlignment: DropdownHorizontalAlignmentsEnum;

  constructor(
    private _toasterDispatcher: ToasterDispatcher,
    private _masterListItemBuilder: MasterListItemBuilder,
    private _dialogService: DialogService,
    private _featuresDataStore: FeaturesDataStore,
    private _globalBannerService: GlobalBannerService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingDispatcher: TrackingDispatcher
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.entityData && this.entityData) {
      this.tokenizedValues = this._masterListItemBuilder.buildTokenizedValues(
        this.entityData
      );
    }
  }

  ngOnInit() {
    this._deletedTrackingAction = this._trackingActionBuilder.buildDeleted(
      this.trackingScreenName
    );

    this.actionsHorizontalAlignment = DropdownHorizontalAlignmentsEnum.right;
  }

  onQuickAction(evt: Event, action: IMasterListItemAction) {
    evt.stopPropagation();
    evt.preventDefault();

    this._featuresDataStore
      .getFeatureToggles()
      .subscribe((toggles: Toggles) => {
        if (this.featureName) {
          const actionFeatureNames: string[] = action.featureNames || [];
          const actionFeatureAllAccess = every(
            [this.featureName, ...actionFeatureNames],
            (actionFeatureName: string) =>
              toggles.isAllAccess(actionFeatureName)
          );
          const actionFeatureNoAccess = find(
            [this.featureName, ...actionFeatureNames],
            (actionFeatureName: string) => toggles.isNoAccess(actionFeatureName)
          );
          const actionFeatureReadOnly = find(
            [this.featureName, ...actionFeatureNames],
            (actionFeatureName: string) =>
              toggles.isReadOnlyAccess(actionFeatureName)
          );

          if (actionFeatureAllAccess) {
            this.emitAction(action);
          } else if (actionFeatureNoAccess || actionFeatureReadOnly) {
            this._dialogService.openGrowthDialog(
              BannerCanvasTypesEnum.feature_lock,
              actionFeatureNoAccess || actionFeatureReadOnly
            );
          }
        } else {
          this.emitAction(action);
        }
      });
  }

  emitAction(action: IMasterListItemAction) {
    if (action.deleteAction) {
      this.onDeleteClick();
    }

    this.onAction.emit(action);
  }

  onDeleteClick() {
    const notifyOfDeleteOptions = this.notifyOfDeleteOptions;

    if (!!notifyOfDeleteOptions) {
      let deletedFromNetwork = false;
      let deleteCanceled = false;
      const entity = this.notifyOfDeleteOptions.entity;
      const toastConfig: IToasterConfig = {
        type: 'delete-entity',
        component: DeleteEntityToastComponent,
        data: notifyOfDeleteOptions.context,
        onShow: () => {
          this.notifyOfDeleteOptions.dispatcher.deleteEntityFromStore(entity);
          this.notifyOfDeleteOptions.dispatcher.removeFromSelectedList(entity);
        },
        onPrimaryAction: () => {
          deleteCanceled = true;
          this.notifyOfDeleteOptions.dispatcher.saveEntityToStore(
            entity,
            false,
            this.listFilter
          );
        },
        onDismiss: () => {
          if (!deletedFromNetwork && !deleteCanceled) {
            deletedFromNetwork = true;
            this.notifyOfDeleteOptions.dispatcher
              .deleteEntityFromNetwork(entity)
              .pipe(take(1))
              .subscribe(noop, (err: IError) =>
                this._globalBannerService.showSystemErrorBanner(err.error)
              );
          }
        },
      };

      this._toasterDispatcher.showToast(toastConfig);
    }

    this._trackingDispatcher.trackEvent(
      (this.route && this.route.trackingObject) || this.trackingObject,
      this._deletedTrackingAction,
      { position: this.position, ...this.trackingAdditionalProperties }
    );
  }

  onBulkActionClick(event: Event) {
    if (this.bulkActionControl) {
      // prevent checkbox set its value before store is updated
      event.stopPropagation();
      event.preventDefault();

      this.onSelectClick.emit(!this.bulkActionControl.value);
    }
  }

  ngOnDestroy() {
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      this.featureName
    );

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
